import React, { useState, useEffect, useCallback } from "react";
import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import { AdminPageBody, AdminTablePanel } from "../components/AdminPageShell";

import WithdrawalDetailModal from "./components/withdrawals/WithdrawalDetailModal";
import WithdrawalSummaryCards from "./components/withdrawals/WithdrawalSummaryCards";
import WithdrawalFilters from "./components/withdrawals/WithdrawalFilters";
import WithdrawalTable from "./components/withdrawals/WithdrawalTable";
import { useAdminWithdrawals, useAdminResourcePersons } from "../store/admin";

const requestId = (request) => request?.id || request?._id;

const WithdrawalRequests = () => {
  const {
    withdrawals,
    withdrawalsLoading,
    withdrawalsTotal,
    withdrawalsPages,
    withdrawalDetail,
    fetchWithdrawals,
    fetchWithdrawalById,
    approveWithdrawal,
    rejectWithdrawal,
  } = useAdminWithdrawals();

  const { resourcePersons, fetchResourcePersons } = useAdminResourcePersons();

  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [resourcePersonId, setResourcePersonId] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortOrder, setSortOrder] = useState("newest");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  const stats = {
    total: withdrawalsTotal,
    pending: withdrawals.filter((r) => r.status === "REQUESTED").length,
    approved: withdrawals.filter((r) => r.status === "APPROVED").length,
    paid: withdrawals.filter((r) => r.status === "COMPLETED").length,
    rejected: withdrawals.filter((r) => r.status === "REJECTED").length,
    totalPendingAmount: withdrawals
      .filter((r) => r.status === "REQUESTED")
      .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0),
    totalPaid: withdrawals
      .filter((r) => r.status === "COMPLETED")
      .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0),
  };

  useEffect(() => {
    fetchResourcePersons({ page: 1, limit: 100 });
  }, [fetchResourcePersons]);

  const resourcePersonNameById = useCallback(() => {
    const map = new Map();
    (resourcePersons || []).forEach((person) => {
      map.set(String(person.id), person.name || person.fullName || person.email || "—");
    });
    return map;
  }, [resourcePersons]);

  useEffect(() => {
    let cancelled = false;

    const loadWithdrawals = async () => {
      const result = await fetchWithdrawals({
        page,
        limit,
        status: statusFilter,
        resourcePersonId,
      });

      if (!cancelled && !result.success) {
        showToast(result.error || "Failed to fetch withdrawal requests", false);
      }
    };

    loadWithdrawals();

    return () => {
      cancelled = true;
    };
  }, [fetchWithdrawals, page, limit, statusFilter, resourcePersonId, reloadKey, showToast]);

  const handleApprove = async (id) => {
    const result = await approveWithdrawal(id);
    if (!result.success) {
      showToast(result.error || "Failed to approve withdrawal", false);
      return;
    }

    showToast("Withdrawal approved successfully");
    setReloadKey((value) => value + 1);
  };

  const handleReject = async (id, reason) => {
    const result = await rejectWithdrawal(id, reason);
    if (!result.success) {
      showToast(result.error || "Failed to reject withdrawal", false);
      return;
    }

    showToast("Withdrawal rejected");
    setReloadKey((value) => value + 1);
  };

  const nameById = resourcePersonNameById();

  const filteredRequests = withdrawals
    .map((request) => ({
      ...request,
      resourcePersonName:
        nameById.get(String(request.resourcePersonId)) ||
        request.resourcePersonName ||
        request.name ||
        "—",
    }))
    .filter((r) => {
      if (!search) return true;
      return String(r.resourcePersonName).toLowerCase().includes(search.toLowerCase());
    })
    .sort((a, b) => {
      const dateA = new Date(a.requestedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.requestedAt || b.createdAt || 0).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

  const handleExport = () => {
    const csv = [
      ["Resource Person", "Amount (NGN)", "Payment Method", "Date", "Status"].join(","),
      ...filteredRequests.map((r) =>
        [
          `"${r.resourcePersonName || ""}"`,
          parseFloat(r.amount || 0).toFixed(2),
          new Date(r.requestedAt || r.createdAt).toLocaleDateString("en-GB"),
          r.status,
        ].join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `withdrawal-requests-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleView = async (request) => {
    const id = requestId(request);
    setSelectedRequest(request);
    setIsDetailOpen(true);

    if (!id) return;

    const result = await fetchWithdrawalById(id);
    if (!result.success) {
      showToast(result.error || "Failed to fetch withdrawal details", false);
    }
  };

  return (
    <div>
      <AdminNavBar title="Withdrawal Requests" context="Manage resource person withdrawal requests." />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        <div className="sub-body space-y-6">
          <WithdrawalSummaryCards stats={stats} />

          <WithdrawalFilters
            search={search}
            onSearchChange={setSearch}
            resourcePersonId={resourcePersonId}
            resourcePersons={resourcePersons}
            onResourcePersonIdChange={(value) => {
              setPage(1);
              setResourcePersonId(value.trim());
            }}
            statusFilter={statusFilter}
            onStatusFilterChange={(value) => {
              setPage(1);
              setStatusFilter(value);
            }}
            limit={limit}
            onLimitChange={(value) => {
              setPage(1);
              setLimit(value);
            }}
            sortOrder={sortOrder}
            onSortToggle={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
            onExport={handleExport}
          />

          <AdminTablePanel>
            <div className="overflow-x-auto">
              <WithdrawalTable requests={filteredRequests} isLoading={withdrawalsLoading} onView={handleView} />
            </div>
          </AdminTablePanel>

          {!withdrawalsLoading && withdrawalsPages > 1 && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
              <span>
                Page {page} of {withdrawalsPages}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50"
                  disabled={page >= withdrawalsPages}
                  onClick={() => setPage((value) => Math.min(withdrawalsPages, value + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </AdminPageBody>

      <WithdrawalDetailModal
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedRequest(null);
        }}
        request={withdrawalDetail || selectedRequest}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
};

export default WithdrawalRequests;
